import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  Grid,
  InputLabel,
  Typography,
  MenuItem,
  Select,
  Button,
  IconButton,
} from '@mui/material';
import { searchUser, updateUser } from '../../../store/admin/file-type-configuration/actions';
import useStyles from './styles';
import { checkEnvironment } from '../../../utilities';
import userRoleIds from './user-role-ids';
import AcceptIcon from '../../../assets/icons/accept-icon';
import CancelIcon from '../../../assets/icons/cancel-icon';
import { getEntitlementRolesManagement } from '../../../store/entitlements-role-management/actions';

type Roles = {
  roleId: string;
  roleName: string;
  partyNumber: string;
  isEditable: boolean;
}

const UserAccessManagement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const environment = checkEnvironment();
  const [searchInput, setSearchInput] = useState<string>('');
  const [validUser, setValidUser] = useState<any>(null);
  const [roleId, setRoleId] = useState<string>('');
  const [isRevoke, setIsRevoke] = useState<boolean>(false);
  const [roles, setRoles] = useState<Roles[]>([]);

  const boxProperties = {
    p: 3,
    className: classes.box,
  };

  const handleSearchUser = async () => {
    try {
      const user = await dispatch(searchUser(searchInput));
      setValidUser(user);
    } catch {
      setValidUser(null);
    }
  };

  const addUser = () => {
    dispatch(updateUser({ catrecId: validUser.catrecid, roleId }));
  };

  const revokeUser = () => {
    setIsRevoke(true);
  };
  const acceptRevoke = () => {
    dispatch(updateUser({ catrecId: validUser.catrecid }));
    setIsRevoke(false);
  };
  const cancelRevoke = () => {
    setIsRevoke(false);
  };

  const getRoles = async () => {
    const roles: any = await dispatch(getEntitlementRolesManagement());
    setRoles(roles);
  }

  useEffect(() => {
    getRoles();
  }, []);

  return (
    <Grid container direction="row" alignItems="stretch" className={classes.container}>
      <Grid item xs={12} lg={6}>
        <Box p={3} className={classes.boxText}>
          <InputLabel>Environment</InputLabel>
          <InputLabel>
            {environment}
            <Typography variant="body2" className={classes.text}>
              (Any users added will only be added to the environment which you are currently
              logged into. To add any user to another environment, you must log into that
              environment and add them here as well.)
            </Typography>
          </InputLabel>
        </Box>
        <Box {...boxProperties}>
          <InputLabel>CWS ID</InputLabel>
          <Box className={classes.searchBox}>
            <TextField
              variant="standard"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value as string)}
              className={classes.textBox}
            />
            <Button
              className={classes.button}
              variant="outlined"
              color="primary"
              onClick={() => handleSearchUser()}
            >
              Search User
            </Button>
          </Box>
        </Box>
        {validUser &&
          <Box {...boxProperties}>
            <InputLabel>{' '}</InputLabel>
            <Box className={classes.searchBox}>
              <InputLabel>{validUser?.firstName} {validUser?.lastName} | {validUser?.email}</InputLabel>
            </Box>
          </Box>
        }
      </Grid>
      <Grid container xs={12} lg={12}>
        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Role</InputLabel>
            <Select
              variant="standard"
              value={roleId}
              className={classes.select}
              onChange={(event: any) => setRoleId(event.target.value as string)}
            >
              {roles.length > 0 && roles.map((userRole: any) => (
                <MenuItem value={userRole.roleId}>
                  <span title={userRole.roleName}>{userRole.roleName}</span>
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>
        <Grid item xs={12} lg={6}>
          {userRoleIds.find((u: any) => u.id[environment] === roleId)?.hoverTitle}
        </Grid>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Box className={classes.flexBox} p={3}>
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            onClick={addUser}
            disabled={!validUser || !roleId || isRevoke}
          >
            Add User
          </Button>
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            onClick={revokeUser}
            disabled={!validUser || isRevoke}
          >
            Revoke User
          </Button>
          {isRevoke && (
            <>
              <Typography>
                Are you sure? Once the access is revoked, the user is permanently removed and
                cannot be readded.
              </Typography>
              <IconButton onClick={acceptRevoke} aria-label="accept" size="large">
                <AcceptIcon />
              </IconButton>
              <IconButton onClick={cancelRevoke} aria-label="cancel" size="large">
                <CancelIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default UserAccessManagement;
